import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { css } from '@bts-web/utils-style-engine';
import {
  capitalizeFirstLetter,
  intlAssetValueFormatting,
} from '@bts-web/utils-formatting';
import { Locale, useClientTranslation } from '@bts-web/utils-lokalise';
import { TradeControllerVariants } from '../../../utils';
import {
  errorElementStyles,
  MainTradeFormErrorBox,
} from '../../MainTradeFormErrorBox/MainTradeFormErrorBox';
import { TradeInputErrorType } from '../hooks/useTradeInput';
import { FilledExclamationMark } from '../../../../common/svgIcons';
import { getAssetDisplayValueFromSource } from '../../../../common/utils/amounts/getAssetDisplayValueFromSource';
import { TradeAssetDetails } from '../types';
import { MinimumTradeAmountType } from '../../types';
import { getAppConfig } from '@bts-web/core-app-config';
import { TARGET_PRICE_KEY } from '../../constants';
import { getAssetTradeUrl } from '../../../../route-builders';

export const MainTradeFormError = ({
  transactionType,
  locale,
  errors,
  assetDetails,
  openBuyTab,
  minimumTradeAmount,
}: {
  transactionType: TradeControllerVariants;
  errors: TradeInputErrorType;
  assetDetails: TradeAssetDetails;
  openBuyTab?: () => void;
  locale: Locale;
  minimumTradeAmount: MinimumTradeAmountType;
}) => {
  const { currency } = getAppConfig();

  const isInStakePage = usePathname()?.includes('/stake') ?? false;

  const router = useRouter();

  const params = useSearchParams();

  const { t } = useClientTranslation();

  const { tooHigh, tooLow, customError } = errors;

  const displayMinimumTradeAmount =
    transactionType !== 'stake' && !params.has(TARGET_PRICE_KEY);

  const formatMinimumAmount = useMemo(() => {
    return transactionType === 'swap' || transactionType === 'stake'
      ? getAssetDisplayValueFromSource({
          locale,
          sourceAmount: minimumTradeAmount.ASSET?.value as string,
          sourceType: 'ASSET',
          customSymbol: assetDetails.symbol as string,
        })
      : intlAssetValueFormatting(minimumTradeAmount.FIAT?.value ?? 0, {
          currency: currency,
          locale,
        });
  }, [locale, minimumTradeAmount, currency, assetDetails.symbol]);

  const onBuyClick = () => {
    const buyUrl = getAssetTradeUrl(assetDetails.id, { type: 'buy' });

    if (isInStakePage) {
      router.push(buyUrl);
    } else {
      if (openBuyTab) {
        openBuyTab();
      } else {
        router.push(buyUrl);
      }
    }
  };

  const ErrorAmountView = () => {
    const errorTooLow =
      transactionType === 'savings'
        ? t('inline_error_amount_too_low', {
            min_amount: minimumTradeAmount.FIAT?.value ?? '',
            currency,
          })
        : t('inline_error_amount_too_low');

    if (transactionType === 'stake') {
      return tooHigh ? (
        <div className={css({ height: '16px' })}>
          <span
            className={css(errorElementStyles)}
            data-testid="special-input-field__error"
            data-element="error"
            aria-label="error too high"
          >
            <FilledExclamationMark size={16} />
            {t('stake_error_amount_too_high')}{' '}
            <p
              onClick={onBuyClick}
              className={css({
                textDecoration: 'underline',
                padding: 0,
                color: 'warning.text_primary',
                fontSize: 'caption.small',
                fontWeight: 'caption.small',
                letterSpacing: 'caption.small',
                lineHeight: 'caption.small',
                cursor: 'pointer',
              })}
            >
              <strong>
                {t('buy_asset', { asset: assetDetails.symbol || '' })}
              </strong>
            </p>
          </span>
        </div>
      ) : (
        <div className={css({ height: '16px' })}>
          <span
            className={css(errorElementStyles)}
            data-testid="special-input-field__error"
            data-element="error"
            aria-label="error too high"
          >
            <FilledExclamationMark size={16} />
            {t('stake_error_amount_too_low', {
              minimumStakeableAmount: `${minimumTradeAmount.ASSET?.value} ${assetDetails.symbol}`,
            })}
          </span>
        </div>
      );
    }

    return (
      <MainTradeFormErrorBox
        errors={errors}
        errorTooHigh={t('inline_error_amount_too_high')}
        errorTooLow={errorTooLow}
      />
    );
  };

  if (customError || tooHigh || tooLow) {
    return <ErrorAmountView />;
  }

  return displayMinimumTradeAmount ? (
    <div
      className={css({
        display: 'flex',
        height: '16px',
        color: 'neutrals.text_secondary',
        fontSize: 'caption.small_light',
        fontWeight: 'caption.small_light',
        letterSpacing: 'caption.small_light',
        lineHeight: 'caption.small_light',
      })}
    >
      <p>{`${capitalizeFirstLetter(t('minimum'))}: ${formatMinimumAmount}`}</p>
    </div>
  ) : null;
};
